import { Col, message, Row } from 'antd';
import config from 'config';
import { format } from 'date-fns';
import { DonationReportOutput, useGetDonationReportLazyQuery } from 'gql/types';
import { Button } from 'modules/common/components';
import { useChunkedLoader } from 'modules/common/hooks/useChunkedLoader';
import { useReportFilterOptions } from 'modules/reports/hooks/useReportFilterOptions';
import { useReportFilters } from 'modules/reports/hooks/useReportFilters';
import { generateReportHeaders } from 'modules/reports/utils/generateReportHeaders';
import { mapReportData } from 'modules/reports/utils/mapReportData';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getDonationReportColumns } from './columns';
import { getDonationReportFilters } from './filters';

import { ReportDownloads } from '../shared/ReportDownloads';
import { ReportFilterBar } from '../shared/ReportFilterBar';

const DonationReports: FC = () => {
  const { t } = useTranslation('reports.DonationReports');
  const fileName = `donations_${format(
    new Date(),
    config.DATE_FORMATS.SHORT_DATE
  )}`;

  const [getDonationsReportQuery] = useGetDonationReportLazyQuery({
    fetchPolicy: 'network-only',
  });
  const {
    isLoading,
    data: reports,
    load: loadReports,
  } = useChunkedLoader({
    query: async (input) => {
      const result = await getDonationsReportQuery({ variables: { input } });
      if (result.error) {
        throw Error(result.error.message);
      }
      return {
        meta: result.data?.getDonationReports.meta,
        data: (result.data?.getDonationReports.data ??
          []) as DonationReportOutput[],
      };
    },
    onError: (error) => message.error(error.message),
    onEmpty: () => message.info(t('emptyReport')),
  });

  const columns = useMemo(() => getDonationReportColumns(t), [t]);
  const headers = useMemo(() => generateReportHeaders(columns), [columns]);
  const filterOptions = useReportFilterOptions();
  const filterConfigs = useMemo(
    () => getDonationReportFilters(t, filterOptions),
    [t, filterOptions]
  );

  const {
    values,
    setFilterValue,
    clearFilters,
    optionsByKey,
    filteredData,
    hasActiveFilters,
  } = useReportFilters(reports, filterConfigs);

  const mappedData = useMemo(
    () => mapReportData(filteredData ?? [], columns),
    [filteredData, columns]
  );

  return (
    <Row gutter={[10, 10]}>
      <Col span={24}>
        <Button
          onClick={() => loadReports()}
          loading={isLoading}
          disabled={isLoading}
          type="primary"
        >
          {t('generateCsvReport')}
        </Button>
      </Col>
      <Col span={24}>
        <ReportFilterBar
          configs={filterConfigs}
          values={values}
          optionsByKey={optionsByKey}
          onChange={setFilterValue}
          onClear={clearFilters}
          hasActiveFilters={hasActiveFilters}
          counts={
            reports
              ? {
                  shown: filteredData?.length ?? 0,
                  total: reports.length,
                }
              : undefined
          }
        />
      </Col>
      <Col span={24}>
        <ReportDownloads
          fileName={fileName}
          headers={headers}
          data={mappedData}
        />
      </Col>
    </Row>
  );
};

export default DonationReports;
